import { getPartnerById, type Partner, type PartnerFilters } from '@/api/partners';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { usePartners } from '@/hooks/use-partners';
import { useRecentPartners, type RecentPartner } from '@/hooks/use-recent-partners';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { Button, Card, CardContent, Input } from '@kgc/ui';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { QuickCreatePartnerDrawer } from './QuickCreatePartnerDrawer';

interface PartnerSearchSelectProps {
  onSelect: (partner: Partner) => void;
  filter?: PartnerFilters;
  allowCreate?: boolean;
  variant?: 'grid' | 'list';
  renderExtra?: (partner: Partner) => ReactNode;
  placeholder?: string;
  value?: Partner | null;
  /** FE-A3: show the localStorage MRU "Legutóbbi partnerek" quick-pick row. */
  showRecent?: boolean;
}

export function PartnerSearchSelect({
  onSelect,
  filter,
  allowCreate = true,
  variant = 'grid',
  renderExtra,
  placeholder,
  value,
  showRecent = false,
}: PartnerSearchSelectProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [typeChip, setTypeChip] = useState<'all' | 'individual' | 'company'>('all');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 250);
  // FE-fix (Zeno measured): a beírt keresést SERVER-side adjuk át (a kliens
  // korábban csak a default 20-as page-en szűrt → a 20-on kívüli partnerek
  // (pl. VIP) megtalálhatatlanok voltak). pageSize:50 a gyors-választóhoz.
  // PV-bug-fix: üres search → szerver-oldali hasRentals:true (csak renterek);
  // user-search → hasRentals drop (undefined, teljes lista, mindenki kereshető).
  const listFilter = useMemo<PartnerFilters>(() => {
    const f: PartnerFilters = { ...filter, pageSize: filter?.pageSize ?? 50 };
    const q = debouncedSearch.trim();
    if (q) {
      f.search = q;
      f.hasRentals = undefined;
    } else {
      f.hasRentals = true;
    }
    return f;
  }, [filter, debouncedSearch]);
  const { partners, isLoading, error, refetch } = usePartners(listFilter);
  const { recent, push: pushRecent } = useRecentPartners();

  const handleSelect = useCallback(
    (partner: Partner) => {
      if (showRecent) pushRecent(partner);
      onSelect(partner);
    },
    [showRecent, pushRecent, onSelect]
  );

  const handleRecentPick = useCallback(
    async (r: RecentPartner) => {
      const full = partners.find(p => p.id === r.id);
      if (full) {
        handleSelect(full);
        return;
      }
      // A recent MRU-elem lehet a betöltött page-en kívül → by-ID fetch,
      // hogy ne legyen silent-fail (Zeno measured: partners.find→undefined).
      try {
        const res = await getPartnerById(r.id);
        handleSelect(res.data);
      } catch {
        // stale/törölt recent-elem — csendben elnyeljük (no crash)
      }
    },
    [partners, handleSelect]
  );

  const filteredPartners = useMemo(() => {
    let list = partners;
    // FE-A2: client-side chip filters (type + availability) on the loaded list.
    if (typeChip !== 'all') list = list.filter(p => p.type === typeChip);
    if (availableOnly) list = list.filter(p => p.status !== 'blocked' && p.status !== 'inactive');
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        p =>
          p.name.toLowerCase().includes(q) ||
          p.email?.toLowerCase().includes(q) ||
          p.phone?.includes(q) ||
          p.taxNumber?.includes(q) ||
          p.city?.toLowerCase().includes(q) ||
          p.maskedIdCardNumber?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [search, typeChip, availableOnly, partners]);

  const handleCreated = (partner: Partner) => {
    void refetch();
    onSelect(partner);
  };

  return (
    <div className="space-y-4">
      {/* Search with clear */}
      <div className="relative max-w-md">
        <Input
          placeholder={placeholder ?? t('partner.quickCreate.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-testid="partner-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Clear search"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>

      {/* FE-A3: recent partners MRU quick-pick (opt-in, localStorage) */}
      {showRecent && recent.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            {t('partner.searchFilter.recentTitle')}
          </p>
          <div className="flex flex-wrap gap-2">
            {recent.map(r => (
              <button
                key={r.id}
                type="button"
                data-testid={`recent-partner-${r.id}`}
                onClick={() => void handleRecentPick(r)}
                className="rounded-full border border-gray-300 px-3 py-1 text-sm text-foreground transition-colors hover:bg-muted dark:border-[#2d7d8a]"
              >
                {r.name}
                {r.city ? <span className="ml-1 text-muted-foreground">· {r.city}</span> : null}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* FE-A2: filter chips (type + availability) */}
      <div
        className="flex flex-wrap items-center gap-2"
        role="group"
        aria-label={t('partner.searchFilter.ariaLabel')}
      >
        <FilterChip
          active={typeChip === 'all'}
          label={t('partner.searchFilter.typeAll')}
          onClick={() => setTypeChip('all')}
        />
        <FilterChip
          active={typeChip === 'individual'}
          label={t('partner.searchFilter.typeIndividual')}
          onClick={() => setTypeChip('individual')}
        />
        <FilterChip
          active={typeChip === 'company'}
          label={t('partner.searchFilter.typeCompany')}
          onClick={() => setTypeChip('company')}
        />
        <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
        <FilterChip
          active={availableOnly}
          label={t('partner.searchFilter.availableOnly')}
          onClick={() => setAvailableOnly(v => !v)}
        />
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="py-8 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Partner list/grid */}
      {!isLoading &&
        !error &&
        (variant === 'grid' ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredPartners.map(partner => (
              <PartnerGridCard
                key={partner.id}
                partner={partner}
                isSelected={value?.id === partner.id}
                onSelect={() => handleSelect(partner)}
                renderExtra={renderExtra}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-1">
            {filteredPartners.map(partner => (
              <PartnerListItem
                key={partner.id}
                partner={partner}
                isSelected={value?.id === partner.id}
                onSelect={() => handleSelect(partner)}
                renderExtra={renderExtra}
              />
            ))}
          </div>
        ))}

      {/* Create new partner link — always visible when not loading */}
      {allowCreate && !isLoading && (
        <div
          className={cn(
            'rounded-lg border-2 border-dashed p-4 text-center',
            filteredPartners.length === 0
              ? 'border-gray-200 dark:border-[#2d7d8a]'
              : 'border-gray-100 dark:border-gray-800'
          )}
        >
          <Button variant="link" onClick={() => setDrawerOpen(true)}>
            {t('partner.quickCreate.notFound')}
          </Button>
        </div>
      )}

      {/* Quick Create Drawer */}
      {allowCreate && (
        <QuickCreatePartnerDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          onCreated={handleCreated}
          initialName={search}
        />
      )}
    </div>
  );
}

/* ---- Grid Card (extracted from rental PartnerStep pattern) ---- */

function PartnerGridCard({
  partner,
  isSelected,
  onSelect,
  renderExtra,
}: {
  partner: Partner;
  isSelected: boolean;
  onSelect: () => void;
  renderExtra?: (partner: Partner) => ReactNode;
}) {
  const { t } = useTranslation();
  const isBlocked = partner.status === 'blocked';

  return (
    <Card
      data-testid={`partner-option-${partner.id}`}
      className={cn(
        'transition-all',
        isBlocked
          ? 'cursor-not-allowed border-red-300 dark:border-red-700 opacity-60'
          : 'cursor-pointer hover:shadow-md',
        isSelected ? 'ring-2 ring-primary shadow-lg' : '',
        partner.status === 'warning' && !isSelected && 'border-orange-300 dark:border-orange-700'
      )}
      onClick={isBlocked ? undefined : onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate font-medium text-gray-900 dark:text-white">{partner.name}</h3>
              <PartnerStatusBadge status={partner.status} />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {partner.type === 'company' ? t('partner.company') : t('partner.individual')}
              {partner.city && ` — ${partner.city}`}
            </p>
          </div>
          {isSelected && <CheckIcon />}
        </div>

        <div className="mt-3 space-y-1 text-sm text-muted-foreground">
          {partner.phone && <DetailRow label={t('partner.phone')} value={partner.phone} />}
          {partner.email && <DetailRow label={t('partner.email')} value={partner.email} />}
          {partner.taxNumber && (
            <DetailRow label={t('partner.taxNumber')} value={partner.taxNumber} />
          )}
          {partner.type === 'individual' && partner.birthDate && (
            <DetailRow
              label={t('partner.birthDate')}
              value={new Date(partner.birthDate).toLocaleDateString('hu-HU')}
            />
          )}
          {partner.address && (
            <DetailRow
              label={t('partner.address')}
              value={[partner.postalCode, partner.city, partner.address].filter(Boolean).join(', ')}
            />
          )}
        </div>

        {renderExtra?.(partner)}
      </CardContent>
    </Card>
  );
}

/* ---- List Item (for POS) ---- */

function PartnerListItem({
  partner,
  isSelected,
  onSelect,
  renderExtra,
}: {
  partner: Partner;
  isSelected: boolean;
  onSelect: () => void;
  renderExtra?: (partner: Partner) => ReactNode;
}) {
  const isBlocked = partner.status === 'blocked';

  return (
    <button
      onClick={isBlocked ? undefined : onSelect}
      disabled={isBlocked}
      className={cn(
        'w-full rounded-lg border p-3 text-left transition-colors',
        isBlocked ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50 dark:hover:bg-[#0f3d47]',
        isSelected && !isBlocked && 'ring-2 ring-primary bg-primary/5'
      )}
    >
      <p
        className={cn(
          'font-medium',
          isBlocked ? 'text-muted-foreground' : 'text-gray-900 dark:text-white'
        )}
      >
        {partner.name}
      </p>
      <p className="text-sm text-muted-foreground">
        {partner.type === 'company'
          ? [partner.taxNumber, partner.city].filter(Boolean).join(' — ')
          : [
              partner.birthDate
                ? new Date(partner.birthDate).toLocaleDateString('hu-HU')
                : undefined,
              partner.phone ?? partner.email,
            ]
              .filter(Boolean)
              .join(' — ')}
      </p>
      {/* M1: address line for both company and individual */}
      {partner.city && (
        <p className="text-xs text-muted-foreground">
          {partner.postalCode ? `${partner.postalCode} ` : ''}
          {partner.city}
          {partner.address ? `, ${partner.address}` : ''}
        </p>
      )}
      {renderExtra?.(partner)}
    </button>
  );
}

/* ---- Shared sub-components ---- */

function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-white'
          : 'border-gray-300 text-muted-foreground hover:bg-muted dark:border-[#2d7d8a]'
      )}
    >
      {label}
    </button>
  );
}

function PartnerStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  if (!status || status === 'active') return null;

  const config: Record<string, { label: string; className: string }> = {
    trusted: {
      label: t('rental.wizard.trustedStatus'),
      className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    },
    warning: {
      label: t('rental.wizard.warningStatus'),
      className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    },
    blocked: {
      label: t('rental.wizard.blockedStatus'),
      className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    },
    inactive: {
      label: t('rental.wizard.inactiveStatus'),
      className: 'bg-gray-100 text-gray-600 dark:bg-[#1f5f6b] dark:text-[#ccfbf1]',
    },
  };

  const cfg = config[status];
  if (!cfg) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        cfg.className
      )}
    >
      {cfg.label}
    </span>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <p className="flex items-center gap-2 truncate">
      <span className="text-gray-400 dark:text-[#99f6e4]">{label}</span>
      {value}
    </p>
  );
}

function CheckIcon() {
  return (
    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white">
      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  );
}
